import React, { useState } from "react";
import { Box, Button, Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import CheckIcon from "@mui/icons-material/Check";
import { PrimaryButton } from "../authShared";

// ─────────────────────────────────────────────────────────────────────────────
// RecoveryCodeView — the one moment a recovery code is visible
//
// The server stores only a keyed hash, so this screen is the only copy there
// will ever be. It says so, offers Copy and a .txt download, and the Continue
// button stays disabled until the person ticks "I've saved it".
//
// The code lives in component state only: never in localStorage, never in the
// URL, never logged. Leaving the screen drops it.
//
// `tone`: "light" on the sign-in pages, "dark" inside the dashboard.
// ─────────────────────────────────────────────────────────────────────────────

const PALETTE = {
  light: {
    text: "var(--fn-navy)", muted: "var(--fn-muted-l)", box: "var(--fn-white)",
    border: "var(--fn-line-l)", accent: "var(--fn-teal-ink)", warnBg: "rgba(244,183,64,.12)", warnText: "#7A5410"
  },
  dark: {
    text: "#EFE3CC", muted: "#ABA5C9", box: "#0B0A1F",
    border: "rgba(239,227,204,0.20)", accent: "#2BBDAF", warnBg: "rgba(244,183,64,0.10)", warnText: "#F2D9A6"
  }
};

function downloadText(code) {
  const text =
    "Fennira recovery code\n\n" +
    `${code}\n\n` +
    "Use it at https://studio.fennira.com/recover if you lose all your passkeys.\n" +
    "It works once. Keep it somewhere safe and private, like a password manager.\n" +
    `Saved: ${new Date().toISOString().slice(0, 10)}\n`;
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "fennira-recovery-code.txt";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function RecoveryCodeView({ code, tone = "light", title, intro, continueLabel = "Continue", onContinue }) {
  const c = PALETTE[tone];
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked: the code is on screen to copy by hand */ }
  };

  const btn = {
    textTransform: "none", fontWeight: 700, borderRadius: "999px", px: 2.25, minHeight: 40,
    color: c.text, border: `1.5px solid ${c.border}`,
    "&:hover": { borderColor: c.accent, bgcolor: "transparent" }
  };

  return (
    <Box>
      {title && (
        <Typography component="h2" sx={{
          fontFamily: tone === "light" ? "var(--fn-display)" : "'Bricolage Grotesque', 'DM Sans', sans-serif",
          fontWeight: 800, fontSize: { xs: 26, sm: 30 }, lineHeight: 1.1, color: c.text, mb: 1.25
        }}>
          {title}
        </Typography>
      )}
      <Typography sx={{ color: c.muted, fontSize: 15, lineHeight: 1.55, mb: 2.5 }}>
        {intro || "If you ever lose your passkeys, this code gets you back in. It's shown only now, and it works once."}
      </Typography>

      <Box
        aria-label="Your recovery code"
        sx={{
          p: { xs: 2, sm: 2.5 }, borderRadius: "14px", bgcolor: c.box, border: `1.5px dashed ${c.border}`,
          fontFamily: "'JetBrains Mono', ui-monospace, Consolas, monospace",
          fontSize: { xs: 18, sm: 21 }, fontWeight: 500, letterSpacing: ".04em", color: c.text,
          textAlign: "center", userSelect: "all", lineHeight: 1.6
        }}
      >
        {/* Wraps only BETWEEN groups, never inside one, so a line break can't
            be mistaken for a missing character when someone copies it by hand. */}
        {code.split("-").map((group, i, all) => (
          <Box key={i} component="span" sx={{ whiteSpace: "nowrap" }}>
            {group}{i < all.length - 1 ? "-" : ""}
            {i < all.length - 1 && <wbr />}
          </Box>
        ))}
      </Box>

      <Stack direction="row" spacing={1.25} sx={{ mt: 1.75, flexWrap: "wrap", rowGap: 1 }}>
        <Button onClick={copy} startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />} sx={btn}>
          {copied ? "Copied" : "Copy"}
        </Button>
        <Button onClick={() => downloadText(code)} startIcon={<DownloadIcon />} sx={btn}>
          Download .txt
        </Button>
      </Stack>

      <Box sx={{ mt: 2.5, p: 1.75, borderRadius: "12px", bgcolor: c.warnBg, color: c.warnText, fontSize: 14, lineHeight: 1.5 }}>
        Store it in a password manager or print it. Don't send it by email or chat. Anyone who has it can get into
        your account, and Fennira will never ask you for it.
      </Box>

      <FormControlLabel
        sx={{ mt: 1.5, color: c.text, "& .MuiTypography-root": { fontSize: 15 } }}
        control={
          <Checkbox checked={saved} onChange={(e) => setSaved(e.target.checked)}
            sx={{ color: c.muted, "&.Mui-checked": { color: c.accent } }} />
        }
        label="I've saved my recovery code"
      />

      {onContinue && (
        <Box sx={{ mt: 1.5 }}>
          {tone === "light" ? (
            <PrimaryButton disabled={!saved} onClick={onContinue}>{continueLabel}</PrimaryButton>
          ) : (
            <Button fullWidth disabled={!saved} onClick={onContinue} variant="contained"
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: "999px", minHeight: 48,
                bgcolor: c.accent, color: "#12112B", boxShadow: "none",
                "&:hover": { bgcolor: "#2BBDAF", boxShadow: "none" },
                "&.Mui-disabled": { bgcolor: c.accent, color: "#12112B", opacity: .45 } }}>
              {continueLabel}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
